/**
 * Colours of the lights canvas, read from the overlay's CSS custom properties (graphLights.css sets `--lights-*` per theme
 * from the dashboard's `--arta-*` tokens, including color-mix() blends) so the canvas, the legend swatches and the side panel
 * share one palette. Each token is resolved through a probe element (`color: var(--token)`), so blends come back as plain rgb().
 *
 * readTheme(el, mode) -> {mode, additive, bg, rgb: {bg, cause, symptom, fact, leading, ember, denied, faded, edge, edgeLit,
 *                         rulesOut, label, cluster, selection}, faintEdgeAlpha, labelAlpha, clusterRingAlpha, clusterFillAlpha,
 *                         glowAlpha, softFill, outline}
 * toneRgb(theme, role, tone) -> [r, g, b]
 */
import { ROLES, THEME_MODES, TONES } from "./lightsConstants";

export const THEME_TOKENS = Object.freeze({
  bg: "--lights-canvas", cause: "--lights-cause", symptom: "--lights-symptom", fact: "--lights-fact", leading: "--lights-leading",
  ember: "--lights-ember", denied: "--lights-denied", faded: "--lights-faded", edge: "--lights-edge", edgeLit: "--lights-edge-lit",
  rulesOut: "--lights-rules-out", label: "--lights-label", cluster: "--lights-cluster", selection: "--lights-selection",
});

/** Used when a token is unset (tests, a stage outside the overlay); the resolved values of graphLights.css. */
const FALLBACK = Object.freeze({
  [THEME_MODES.NIGHT]: Object.freeze({
    bg: "#121d2b", cause: "#f59e0b", symptom: "#06b6d4", fact: "#c6dde3", leading: "#fcdeb3", ember: "#ef4444", denied: "#ef4444",
    faded: "#6e7687", edge: "#8d96a7", edgeLit: "#06b6d4", rulesOut: "#ef4444", label: "#f1f5f9", cluster: "#67e8f9", selection: "#ffffff",
  }),
  [THEME_MODES.DAY]: Object.freeze({
    bg: "#e3eff5", cause: "#b37713", symptom: "#0b809d", fact: "#616978", leading: "#cb6525", ember: "#dc2626", denied: "#dc2626",
    faded: "#b6bcc6", edge: "#6e7687", edgeLit: "#0b809d", rulesOut: "#dc2626", label: "#1a1d26", cluster: "#0891b2", selection: "#0891b2",
  }),
});

/**
 * Per theme: additive glow at night; by day saturated fills on cool paper with a crisp outline and a soft coloured halo. Alphas of
 * the faint (not traversed) edges, the labels, the cluster ring and disc, the halo; softFill = how far an unlit day fill is mixed
 * toward white; fixedAlphaScale lifts the fixed-alpha tones (ruled out, faded) so they still read on a light canvas.
 */
const MODE_STYLE = Object.freeze({
  [THEME_MODES.NIGHT]: { additive: true, faintEdgeAlpha: 0.16, labelAlpha: 0.92, clusterRingAlpha: 0.16, clusterFillAlpha: 0, glowAlpha: 1, softFill: 0, outline: 0, fixedAlphaScale: 1 },
  [THEME_MODES.DAY]: { additive: false, faintEdgeAlpha: 0.4, labelAlpha: 1, clusterRingAlpha: 0.42, clusterFillAlpha: 0.1, glowAlpha: 0.6, softFill: 0.4, outline: 1.25, fixedAlphaScale: 1.8 },
});

const HEX_SHORT = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX_LONG = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i;
const RGB_FN = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i;

/** "#rgb", "#rrggbb" or "rgb(a)(r, g, b)" -> [r, g, b], null when unreadable. */
export function parseColor(text) {
  const t = String(text || "").trim();
  const short = HEX_SHORT.exec(t);
  if (short) return short.slice(1, 4).map((h) => parseInt(h + h, 16));
  const long = HEX_LONG.exec(t);
  if (long) return long.slice(1, 4).map((h) => parseInt(h, 16));
  const fn = RGB_FN.exec(t);
  if (fn) return fn.slice(1, 4).map((v) => Math.max(0, Math.min(255, Math.round(Number(v)))));
  return null;
}

export const mixRgb = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
export const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];
const LIT_WHITE_MIX = 0.18;
const LEADING_WHITE_MIX = 0.55;
const EMBER_DARK_MIX = 0.35;
const EMBER_DAY_DARK_MIX = 0.2;

/** Resolves every token through a probe span inside `el` (color-mix() and var() come back as rgb()); null when no DOM. */
function resolveTokens(el) {
  if (!el || typeof window === "undefined" || typeof window.getComputedStyle !== "function" || typeof document === "undefined") return null;
  const probe = document.createElement("span");
  probe.setAttribute("aria-hidden", "true");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  el.appendChild(probe);
  const out = {};
  try {
    Object.keys(THEME_TOKENS).forEach((name) => {
      probe.style.color = `var(${THEME_TOKENS[name]})`;
      out[name] = parseColor(window.getComputedStyle(probe).color);
    });
  } finally {
    probe.remove();
  }
  return out;
}

/** The palette of `mode`, read from `el` (null el: fallbacks only). */
export function readTheme(el, mode = THEME_MODES.NIGHT) {
  const safeMode = MODE_STYLE[mode] ? mode : THEME_MODES.NIGHT;
  const fallback = FALLBACK[safeMode];
  const resolved = resolveTokens(el) || {};
  const rgb = {};
  Object.keys(THEME_TOKENS).forEach((name) => { rgb[name] = resolved[name] || parseColor(fallback[name]); });
  return { mode: safeMode, ...MODE_STYLE[safeMode], bg: `rgb(${rgb.bg.join(",")})`, rgb };
}

const BASE_OF_ROLE = Object.freeze({ [ROLES.CAUSE]: "cause", [ROLES.SYMPTOM]: "symptom", [ROLES.FACT]: "fact" });

/** RGB of one (role, tone) in the theme. Causes are amber, symptoms cyan, facts slate; the diagnosis states recolour them. */
export function toneRgb(theme, role, tone) {
  const base = theme.rgb[BASE_OF_ROLE[role]] || theme.rgb.fact;
  switch (tone) {
    case TONES.LIT:
    case TONES.EVALUATING:
      return theme.additive ? mixRgb(base, WHITE, LIT_WHITE_MIX) : base;
    case TONES.LEADING:
      return theme.additive ? mixRgb(base, WHITE, LEADING_WHITE_MIX) : theme.rgb.leading;
    case TONES.EMBER:
      // Darkened in both modes: a ruled-out cause is a dark red coal, never a pink that would wash out on paper.
      return mixRgb(theme.rgb.ember, BLACK, theme.additive ? EMBER_DARK_MIX : EMBER_DAY_DARK_MIX);
    case TONES.DENIED:
      return theme.rgb.denied;
    case TONES.FADED:
      return theme.rgb.faded;
    default:
      return base;
  }
}

export const rgbCss = (rgb, alpha = 1) => `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
